import { ColorMatrixFilter, Container, Graphics, Sprite, Text } from 'pixi.js';
import type { DigDug, DigOffer, DigState } from '@goodtime/shared';
import { AudioBus } from '../../audio/AudioBus';
import { bankLabel, bombNote, canBank, canDig, DIG_EXPLAIN, digCounters, digLead, dugAt, hitBomb, noBombs } from '../../logic/minigames/dig';
import { destination } from '../../logic/minigames/core';
import { GAME_SOUNDS } from '../../logic/minigames/sounds';
import { ROAD_SOUNDS } from '../../logic/road/sounds';
import { animate, displayStyle, ease, prefersReducedMotion } from '../board/ui';
import { CounterRow, gridCells } from './pieces';
import { TurnGameDialog } from './TurnGameDialog';
import type { MiniGameDeps } from './types';
import type { GameView } from '../../logic/minigames/core';

type DigReveal = DigDug | null;

interface SquareView {
  root: Container;
  /** The turf (covered) or the dug hole. */
  ground: Sprite;
  drawn: Graphics;
  /** What was found (coins or the bomb) and its amount, popped in together. */
  shown: Container;
  find: Sprite;
  caption: Text;
  size: number;
}

/**
 * Dig for coins: a field of sixteen turf squares. Tap one to dig it: coins
 * go into the pot, and BANK keeps the pot. A bomb loses the pot and ends
 * the dig; it pays a little for each safe dig, said plainly as a loss.
 * Only the squares dug ever show what they held. Tapping the counters or a
 * dug square says what they mean.
 *
 * The treasure map's dig (the story) has no bombs: a smaller field, every
 * square coins, dug to the end with no BANK.
 */
export class DigDialog extends TurnGameDialog<DigOffer, DigState, DigReveal> {
  private squares: SquareView[] = [];
  private readonly counters: CounterRow;
  private readonly faded = new ColorMatrixFilter();
  /** A square being dug, until its answer shows. */
  private digging: number | null = null;

  constructor(deps: MiniGameDeps) {
    super(deps, 'dig', 'DIG FOR COINS');
    this.counters = new CounterRow(['Dug', 'Pot'], (at) => this.explain(DIG_EXPLAIN.counters(this.offer?.setup), at));
    this.faded.saturate(-0.35);
    this.faded.brightness(0.8, true);
    this.content.addChild(this.counters);
  }

  protected begin(offer: DigOffer): void {
    // The title is the layer's: the friend's own name, or DIG FOR TREASURE
    // with no bombs (the registry's titleFor).
    this.squares.forEach((s) => s.root.destroy({ children: true }));
    this.digging = null;
    const n = offer.setup?.cells ?? 16;
    this.squares = Array.from({ length: n }, (_, i) => {
      const root = new Container();
      const ground = new Sprite();
      ground.anchor.set(0.5);
      const drawn = new Graphics();
      const find = new Sprite();
      find.anchor.set(0.5);
      find.visible = false;
      const caption = new Text({ text: '', style: displayStyle(18) });
      caption.anchor.set(0.5);
      caption.visible = false;
      const shown = new Container();
      shown.addChild(find, caption);
      root.addChild(drawn, ground, shown);
      root.eventMode = 'static';
      root.cursor = 'pointer';
      root.on('pointertap', () => this.onPiece(i));
      this.content.addChild(root);
      return { root, ground, drawn, shown, find, caption, size: 60 };
    });
  }

  protected onPiece(index: number): void {
    const dug = dugAt(this.game, index);
    const sq = this.squares[index];
    if (dug?.kind === 'coins' && sq) {
      this.explain(DIG_EXPLAIN.square(dug.coins), { x: sq.root.x, y: sq.root.y - sq.size * 0.4 });
      return;
    }
    if (!canDig(this.game, index) || !sq) return;
    this.digging = index;
    this.settling = true;
    this.act('dig', String(index), index);
    void animate(160, (t) => sq.root.scale.set(1 - 0.08 * Math.sin(t * Math.PI)));
  }

  protected override actionLabel(): string | null {
    return noBombs(this.offer?.setup) ? null : bankLabel(this.game);
  }

  protected override onActionTap(): void {
    if (!canBank(this.game, this.offer?.setup)) return;
    this.settling = true;
    this.act('bank');
  }

  protected override wonPrize(): boolean {
    return !hitBomb(this.game);
  }

  protected override noteText(): string {
    const where = destination(this.game.toPot, this.game.prize);
    return hitBomb(this.game) ? bombNote(this.offer?.setup, where) : where;
  }

  protected override counterValues(): string[] | null {
    if (!this.counters.visible) return null;
    const c = digCounters(this.game);
    return [`Dug ${c.dug}`, `Pot ${c.pot}`];
  }

  protected override onAnswered(prev: GameView<DigState, DigReveal>, next: GameView<DigState, DigReveal>): void {
    this.digging = null;
    const dug = next.last !== prev.last && !next.error ? next.last : null;
    if (!dug) {
      // A bank, a refusal or a lost answer: nothing dug up to show.
      this.settling = false;
      return;
    }
    AudioBus.shared.play(GAME_SOUNDS.dig, { volume: 0.85 });
    const sq = this.squares[dug.index];
    if (sq) void animate(380, (t) => sq.shown.scale.set(0.5 + 0.5 * ease.outBack(t)));
    if (dug.kind === 'bomb') {
      window.setTimeout(() => AudioBus.shared.play(GAME_SOUNDS.bomb, { volume: 0.9 }), 140);
      void this.boom().then(() => this.reveal());
      return;
    }
    const coin = ROAD_SOUNDS.coins[dug.index % ROAD_SOUNDS.coins.length]!;
    window.setTimeout(() => AudioBus.shared.play(coin, { volume: 0.75 }), 180);
    if (next.phase === 'over') window.setTimeout(() => this.reveal(), 500);
    else this.settling = false;
  }

  /** The bomb goes off: the field shakes. */
  private async boom(): Promise<void> {
    const x = this.content.x;
    await animate(prefersReducedMotion() ? 0 : 420, (t) => {
      this.content.x = x + Math.sin(t * Math.PI * 9) * 7 * (1 - t);
    });
    this.content.x = x;
    await new Promise((r) => setTimeout(r, prefersReducedMotion() ? 0 : 350));
  }

  protected leadText(): string {
    if (this.settling && this.game.phase === 'over') return this.game.state?.ended === 'bomb' ? 'BOOM!' : 'Digging…';
    return digLead(this.game, this.offer?.setup);
  }

  protected pieceLabels(): string[] {
    return this.squares.map((_, i) => {
      const d = dugAt(this.game, i);
      if (!d) return `Square ${i + 1}`;
      return d.kind === 'bomb' ? `Square ${i + 1}: bomb` : `Square ${i + 1}: ${d.coins}`;
    });
  }

  protected renderPieces(y: number, width: number, maxHeight: number): number {
    const over = this.game.phase === 'over' && !this.settling;
    const countersH = over ? 0 : this.counters.rowHeight + 8;
    const n = this.squares.length;
    const cols = n === 16 ? 4 : Math.ceil(Math.sqrt(n));
    const { cells, size, height } = gridCells(n, cols, width, 92, 4, 1, maxHeight - countersH);
    const covered = this.deps.texture('road:dig_covered');
    const hole = this.deps.texture('road:dig_hole');
    const coins = this.deps.texture('road:dig_coins');
    const bomb = this.deps.texture('road:dig_bomb') ?? this.deps.texture('road:bomb');
    this.squares.forEach((sq, i) => {
      const d = dugAt(this.game, i);
      sq.size = size;
      sq.root.position.set(cells[i]!.x, y + cells[i]!.y);
      sq.root.hitArea = { contains: (px: number, py: number) => Math.abs(px) <= size / 2 && Math.abs(py) <= size / 2 };
      const tex = d ? hole : covered;
      sq.drawn.clear();
      if (tex) {
        sq.ground.texture = tex;
        sq.ground.width = sq.ground.height = size * 1.06;
        sq.ground.visible = true;
      } else {
        sq.ground.visible = false;
        sq.drawn
          .roundRect(-size * 0.46, -size * 0.46, size * 0.92, size * 0.92, size * 0.14)
          .fill({ color: d ? 0x6a4020 : 0x5aa832 })
          .stroke({ color: 0x0b1838, width: 3 });
      }
      sq.find.visible = !!d;
      sq.caption.visible = d?.kind === 'coins';
      if (d) {
        const art = d.kind === 'bomb' ? bomb : coins;
        if (art) sq.find.texture = art;
        const k = d.kind === 'bomb' ? 0.7 : 0.62;
        sq.find.width = sq.find.height = size * k;
        sq.find.position.set(0, d.kind === 'bomb' ? -size * 0.08 : -size * 0.04);
        sq.caption.text = `+${d.coins.toLocaleString('en-GB')}`;
        sq.caption.style = displayStyle(Math.max(14, Math.round(size * 0.24)));
        sq.caption.position.set(0, size * 0.3);
      }
      sq.root.cursor = canDig(this.game, i) ? 'pointer' : d?.kind === 'coins' ? 'help' : 'default';
      // Squares never dug fade once the dig is over: nothing says what they held.
      sq.root.filters = over && !d ? [this.faded] : [];
      sq.root.alpha = over && !d ? 0.75 : this.digging === i ? 0.85 : 1;
    });
    this.counters.visible = !over;
    if (over) return y + height;
    const c = digCounters(this.game);
    this.counters.set([String(c.dug), c.pot.toLocaleString('en-GB')], width);
    this.counters.position.set(0, y + height + 8);
    return y + height + countersH;
  }

  protected override render(): void {
    super.render();
    // BANK waits for a pot.
    if (this.game.phase === 'play' && !this.settling) this.main.setEnabled(canBank(this.game, this.offer?.setup));
  }
}
